import React, { useState, useRef, useEffect } from 'react';
import { importTransactions, getImportStatus } from '../api';
import { getAllCats, getAllBills, mk, fc } from '../utils';

/* ── keyword → category auto-mapper ───────────────────────────────────────── */
const CAT_KEYWORDS = {
  gas: [
    'shell',
    'bp',
    'chevron',
    'exxon',
    'mobil',
    'marathon',
    'sunoco',
    'speedway',
    'fuel',
    'gas station',
    'circle k',
    'wawa',
    'kwik',
    'parker',       // Parker's Kitchen (Southeast US)
    'enmarket',     // Enmarket (Southeast US)
    'racetrac',
    'racetrack',
    'murphyusa',
    'murphy express',
    'sheetz',
    'caseys',
    "casey's",
    'pilot',
    'flying j',
    'love\'s',
    'loves travel',
    'gate petroleum',
    'gate',
    'buc-ee',
    'bucees',
    'quiktrip',
    'qt',
    'thorntons',
    'maverik',
    'holiday station',
    'kwikstar',
    'kum & go',
    'pump',         // generic pump references
  ],
  groc: [
    'walmart',
    'kroger',
    'safeway',
    'aldi',
    'trader joe',
    'whole foods',
    'publix',
    'target',
    'costco',
    "sam's club",
    "bj's",
    'grocery',
    'market',
    'food lion',
    'meijer',
    'heb',
    'wegmans',
    'shop rite',
    'stop & shop',
  ],
  food: [
    'mcdonald',
    'burger king',
    'wendy',
    'chick-fil',
    'taco bell',
    'subway',
    'starbucks',
    'chipotle',
    'domino',
    'pizza',
    'doordash',
    'ubereats',
    'grubhub',
    'instacart',
    'restaurant',
    'cafe',
    'diner',
    'sushi',
    'grill',
    'tavern',
    'bar &',
    'kitchen',
    'boba',
    'dunkin',
  ],
  ent: [
    'netflix',
    'hulu',
    'spotify',
    'disney',
    'hbo',
    'apple tv',
    'youtube',
    'amazon prime',
    'twitch',
    'steam',
    'playstation',
    'xbox',
    'amc',
    'regal',
    'cinemark',
    'ticketmaster',
    'stubhub',
  ],
  shop: [
    'amazon',
    'ebay',
    'etsy',
    'shein',
    'fashion nova',
    'h&m',
    'old navy',
    'gap',
    'zara',
    'nike',
    'adidas',
    'best buy',
    'home depot',
    "lowe's",
    'ikea',
    'wayfair',
    'chewy',
  ],
  care: [
    'cvs',
    'walgreens',
    'rite aid',
    'ulta',
    'sephora',
    'salon',
    'barber',
    'gym',
    'planet fitness',
    'anytime fitness',
    'dental',
    'vision',
    'doctor',
    'urgent care',
    'pharmacy',
  ],
};
function guessCategory(desc, importRules = []) {
  const lower = (desc || '').toLowerCase();
  // User-defined rules take priority
  for (const rule of importRules) {
    if (rule.match && lower.includes(rule.match.toLowerCase())) return rule.catId;
  }
  for (const [catId, kws] of Object.entries(CAT_KEYWORDS)) {
    if (kws.some((kw) => lower.includes(kw))) return catId;
  }
  return 'oth';
}

/* ── duplicate detection ────────────────────────────────────────────────────── */
// Normalize a merchant name for fuzzy comparison
function normDesc(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

// Returns true if descriptions are similar enough to be the same merchant
function descSimilar(a, b) {
  const na = normDesc(a), nb = normDesc(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  // Substring match (e.g. "AMAZON.COM" vs "Amazon Marketplace")
  if (na.includes(nb) || nb.includes(na)) return true;
  // First meaningful word matches and length is close (handles "SQ *COFFEE SHOP" vs "Coffee Shop")
  const wordsA = na.split(' ').filter(w => w.length > 2);
  const wordsB = nb.split(' ').filter(w => w.length > 2);
  if (wordsA.length && wordsB.length && wordsA[0] === wordsB[0]) return true;
  return false;
}

/**
 * Given new import rows and the existing varSpend map, mark rows that look
 * like they've already been imported (same month, same amount, similar desc).
 */
function detectDuplicates(rows, varSpend) {
  return rows.map((row) => {
    const existing = varSpend[row.moKey] || {};
    const allEntries = Object.values(existing).flat(); // [{place, amt, ts}, ...]
    const isDuplicate = allEntries.some((e) => {
      if (Math.abs(e.amt - row.amount) > 0.01) return false;
      // Date within 3 days (ts may be imprecise) or description similar
      const rowTs = row.date
        ? new Date(row.date.includes('T') ? row.date : row.date + 'T12:00:00').getTime()
        : null;
      const tsClose = rowTs && e.ts && Math.abs(e.ts - rowTs) < 3 * 86400000;
      return tsClose || descSimilar(e.place, row.desc);
    });
    return isDuplicate ? { ...row, isDuplicate: true, checked: false } : row;
  });
}

/* ── steps ─────────────────────────────────────────────────────────────────── */
const S_INPUT = 'input';
const S_LOADING = 'loading';
const S_CLARIFY = 'clarify'; // ambiguous items need user decision
const S_REVIEW = 'review'; // review table + suggestions

/* ── suggestion type config ────────────────────────────────────────────────── */
const SUG_CFG = {
  add_bill: { em: '📋', label: 'Add as Fixed Bill', color: '#4d96ff' },
  add_subscription: { em: '📱', label: 'Add as Subscription', color: '#7C6FF7' },
  budget_insight: { em: '💡', label: 'Budget Insight', color: 'var(--ylw)' },
};

/* ── bill/sub colors ───────────────────────────────────────────────────────── */
const PALETTE = ['#4d96ff', '#ff6bdf', '#6bcb77', '#ffd93d', '#ff6b6b', '#7C6FF7', '#a0aec0'];

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Import({ state, updateState, showToast, onClose, onComplete, inline }) {
  const {
    varSpend,
    customCats,
    disabledCats,
    fixedBills = [],
    subscriptions = [],
    cards = [],
    incomeLog = [],
    importRules = [],
  } = state;
  const cats = getAllCats(customCats, disabledCats);

  /* steps */
  const [step, setStep] = useState(S_INPUT);
  const [inputMode, setInputMode] = useState('upload');
  const [pasteText, setPasteText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [importAvailable, setImportAvailable] = useState(null); // null=checking, true, false
  const [selectedFileName, setSelectedFileName] = useState('');

  // Check on mount whether the import feature is configured on this server
  useEffect(() => {
    getImportStatus().then((res) => {
      setImportAvailable(res.available !== false);
    }).catch(() => setImportAvailable(true)); // assume ok if check fails
  }, []);

  /* data from Claude */
  const [rows, setRows] = useState([]); // variable spend rows
  const [incomeRows, setIncomeRows] = useState([]); // income / credit rows
  const [ambig, setAmbig] = useState([]); // ambiguous items
  const [sugs, setSugs] = useState([]); // suggestions

  /* clarify step decisions: { [index]: 'variable'|'bill'|'sub'|'skip' } */
  const [clarify, setClarify] = useState({});

  /* suggestion accept/reject: { [id]: bool } */
  const [sugOn, setSugOn] = useState({});

  const anyFileRef = useRef();

  /* ── parse via backend ─────────────────────────────────────────────────── */
  const runParse = async (type, content, mimeType) => {
    setStep(S_LOADING);
    setError('');

    // Pass existing bill/sub names so Claude doesn't re-suggest them
    const existingBills = getAllBills(cards, fixedBills, subscriptions).map((b) => b.name);
    const existingSubs = (subscriptions || []).map((s) => s.name);

    const res = await importTransactions({ type, content, mimeType, existingBills, existingSubs });
    if (res.error) {
      setError(res.error);
      setStep(S_INPUT);
      return;
    }

    const today = new Date();
    const curK = mk(today.getFullYear(), today.getMonth());

    // Parse date at noon local time to avoid UTC midnight → previous-day timezone shift
    const parseDate = (s) => (s ? new Date(s.includes('T') ? s : s + 'T12:00:00') : null);

    const toRows = (arr) =>
      (arr || []).map((t, i) => {
        let moKey = curK;
        const d = parseDate(t.date);
        if (d && !isNaN(d)) moKey = mk(d.getFullYear(), d.getMonth());
        return {
          id: i,
          date: t.date,
          desc: t.description,
          amount: t.amount,
          catId: guessCategory(t.description, importRules),
          moKey,
          checked: true,
        };
      });

    const parsedRows = detectDuplicates(toRows(res.transactions || []), varSpend);
    const parsedAmb = (res.ambiguous || []).map((t, i) => ({
      ...t,
      id: i,
      moKey: (() => {
        const d = parseDate(t.date);
        return !d || isNaN(d) ? curK : mk(d.getFullYear(), d.getMonth());
      })(),
    }));
    const parsedSugs = res.suggestions || [];

    const parsedIncome = (res.income || []).map((t, i) => {
      let moKey = curK;
      const d = parseDate(t.date);
      if (d && !isNaN(d)) moKey = mk(d.getFullYear(), d.getMonth());
      return {
        id: 'inc_' + i,
        date: t.date,
        desc: t.description,
        amount: t.amount,
        moKey,
        checked: true,
      };
    });

    // Pre-accept all suggestions by default
    const initialSugOn = {};
    parsedSugs.forEach((s) => {
      initialSugOn[s.id] = s.type !== 'budget_insight';
    });

    setRows(parsedRows);
    setIncomeRows(parsedIncome);
    setAmbig(parsedAmb);
    setSugs(parsedSugs);
    setSugOn(initialSugOn);
    setClarify({});

    // Skip clarify step if nothing ambiguous
    setStep(parsedAmb.length > 0 ? S_CLARIFY : S_REVIEW);
  };

  /* ── input handlers ────────────────────────────────────────────────────── */
  const handlePaste = () => {
    if (!pasteText.trim()) {
      setError('Paste your bank statement text above');
      return;
    }
    runParse('text', pasteText.trim());
  };

  // Universal file handler — routes to appropriate parser based on extension / MIME type
  const handleAnyFile = async (file) => {
    if (!file) return;
    setError('');
    setSelectedFileName(''); // reset until we know the file is valid
    const ext = file.name.split('.').pop().toLowerCase();
    const mime = file.type || '';

    // ── Image (screenshot) ──────────────────────────────────────────────────
    if (mime.startsWith('image/') || ['png','jpg','jpeg','webp','gif','heic'].includes(ext)) {
      if (file.size > 7 * 1024 * 1024) {
        setError('Image too large — max 7 MB. Try a shorter screenshot or use CSV export.');
        return;
      }
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onerror = () => { setError('Failed to read image — please try again.'); setSelectedFileName(''); };
      reader.onload = (e) => {
        const [header, base64] = e.target.result.split(',');
        runParse('image', base64, header.replace('data:', '').replace(';base64', ''));
      };
      reader.readAsDataURL(file);
      return;
    }

    // ── PDF ──────────────────────────────────────────────────────────────────
    if (ext === 'pdf' || mime === 'application/pdf') {
      if (file.size > 25 * 1024 * 1024) {
        setError('PDF too large — max 25 MB. Try a shorter date range or export as CSV instead.');
        return;
      }
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onerror = () => { setError('Failed to read PDF — please try again.'); setSelectedFileName(''); };
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        runParse('document', base64, 'application/pdf');
      };
      reader.readAsDataURL(file);
      return;
    }

    // ── Excel (.xlsx / .xls) ─────────────────────────────────────────────────
    // xlsx parsing is intentionally not supported client-side (security: known
    // Prototype Pollution vulnerabilities in available parsers with no fix).
    // Guide the user to export CSV instead — every bank and Excel support this.
    if (['xlsx','xls'].includes(ext) || mime.includes('spreadsheet') || mime.includes('excel')) {
      setError(
        'Excel files can\'t be read directly for security reasons. ' +
        'In Excel: File → Save As → CSV (.csv), then upload that file. ' +
        'Your bank\'s website also offers a direct CSV export — look for "Download Transactions".'
      );
      return;
    }

    // ── Text-based formats: CSV, TSV, OFX, QFX, TXT, XML ───────────────────
    if (['csv','tsv','txt','ofx','qfx','xml','json'].includes(ext) ||
        mime === 'text/csv' || mime.startsWith('text/')) {
      // Guard against very large files that would overwhelm Claude
      if (file.size > 1 * 1024 * 1024) {
        setError('File too large — max 1 MB for text files. Try exporting a shorter date range (1–3 months) from your bank.');
        return;
      }
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onerror = () => { setError('Failed to read file — please try again.'); setSelectedFileName(''); };
      reader.onload = (e) => {
        const text = e.target.result.trim();
        if (!text) { setError('File appears to be empty.'); setSelectedFileName(''); return; }
        runParse('text', text);
      };
      reader.readAsText(file);
      return;
    }

    setError(`Unsupported file type (.${ext}). Try CSV, PDF, Excel, OFX, QFX, or a screenshot.`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleAnyFile(e.dataTransfer.files?.[0]);
  };

  /* ── proceed from clarify step ─────────────────────────────────────────── */
  const finishClarify = () => {
    const newRows = [...rows];
    const billsFromAmb = [],
      subsFromAmb = [];

    ambig.forEach((item, i) => {
      const decision = clarify[i] || 'variable';
      if (decision === 'skip') return;
      if (decision === 'bill') {
        billsFromAmb.push({
          name: item.description,
          amount: item.amount,
          date: item.date,
          moKey: item.moKey,
        });
      } else if (decision === 'sub') {
        subsFromAmb.push({ name: item.description, amount: item.amount, date: item.date });
      } else {
        // variable
        newRows.push({
          id: rows.length + i,
          date: item.date,
          desc: item.description,
          amount: item.amount,
          catId: guessCategory(item.description, importRules),
          moKey: item.moKey,
          checked: true,
        });
      }
    });

    // Merge bill/sub suggestions from ambiguous into sugs list for review step
    const extraSugs = [
      ...billsFromAmb.map((b, i) => ({
        id: 'amb_bill_' + i,
        type: 'add_bill',
        name: b.name,
        amount: b.amount,
        dueDay: b.date ? new Date(b.date).getDate() : 1,
        cycle: 'monthly',
        reason: 'You marked this as a recurring bill',
      })),
      ...subsFromAmb.map((s, i) => ({
        id: 'amb_sub_' + i,
        type: 'add_subscription',
        name: s.name,
        amount: s.amount,
        dueDay: s.date ? new Date(s.date).getDate() : 1,
        cycle: 'monthly',
        reason: 'You marked this as a subscription',
      })),
    ];
    const allSugs = [...sugs, ...extraSugs];
    const newSugOn = { ...sugOn };
    extraSugs.forEach((s) => {
      newSugOn[s.id] = true;
    });

    setRows(newRows);
    setSugs(allSugs);
    setSugOn(newSugOn);
    setStep(S_REVIEW);
  };

  /* ── confirm import ────────────────────────────────────────────────────── */
  const handleConfirm = () => {
    const selectedRows = rows.filter((r) => r.checked);
    const selectedIncome = incomeRows.filter((r) => r.checked);

    // Write variable spend
    const updatedVarSpend = { ...varSpend };
    selectedRows.forEach((r) => {
      if (!updatedVarSpend[r.moKey]) updatedVarSpend[r.moKey] = {};
      if (!updatedVarSpend[r.moKey][r.catId]) updatedVarSpend[r.moKey][r.catId] = [];
      updatedVarSpend[r.moKey][r.catId] = [
        ...updatedVarSpend[r.moKey][r.catId],
        {
          place: r.desc,
          amt: Math.round(r.amount * 100) / 100,
          ts: r.date
            ? new Date(r.date.includes('T') ? r.date : r.date + 'T12:00:00').getTime()
            : Date.now(),
        },
      ];
    });

    // Write income entries to the flat incomeLog array
    const newIncomeEntries = selectedIncome.map((r, i) => {
      const raw = r.date ? (r.date.includes('T') ? r.date.split('T')[0] : r.date) : null;
      const date = raw || new Date().toISOString().split('T')[0];
      return { id: 'inc_imp_' + Date.now() + '_' + i, amt: Math.round(r.amount * 100) / 100, date };
    });

    // Apply accepted suggestions
    const acceptedSugs = sugs.filter((s) => sugOn[s.id] && s.type !== 'budget_insight');
    const newBills = [];
    const newSubs = [];
    acceptedSugs.forEach((s, i) => {
      const col = PALETTE[i % PALETTE.length];
      if (s.type === 'add_bill') {
        newBills.push({
          id: 'bill_' + Date.now() + i,
          name: s.name,
          amt: s.amount,
          due: s.dueDay || 1,
          col,
        });
      } else if (s.type === 'add_subscription') {
        newSubs.push({
          id: 'sub_' + Date.now() + i,
          name: s.name,
          amt: s.amount,
          due: s.dueDay || 1,
          cycle: s.cycle || 'monthly',
          col,
        });
      }
    });

    const stateUpdate = { varSpend: updatedVarSpend };
    if (selectedIncome.length) {
      const merged = [...incomeLog, ...newIncomeEntries].sort((a, b) => a.date.localeCompare(b.date));
      stateUpdate.incomeLog = merged;
    }
    if (newBills.length) stateUpdate.fixedBills = [...fixedBills, ...newBills];
    if (newSubs.length) stateUpdate.subscriptions = [...(subscriptions || []), ...newSubs];

    // Build import history entry — store enough to undo
    const importId = 'imp_' + Date.now();
    const summary = {
      id: importId,
      ts: Date.now(),
      date: new Date().toISOString().slice(0, 10),
      txCount: selectedRows.length,
      incomeCount: selectedIncome.length,
      billsAdded: newBills.length,
      subsAdded: newSubs.length,
      totalSpend: Math.round(selectedRows.reduce((s, r) => s + r.amount, 0) * 100) / 100,
      totalIncome: Math.round(selectedIncome.reduce((s, r) => s + r.amount, 0) * 100) / 100,
      // Reversible snapshot — what was added so we can remove it
      _undo: {
        txKeys: selectedRows.map((r) => ({ moKey: r.moKey, catId: r.catId, ts: r.date ? new Date(r.date.includes('T') ? r.date : r.date + 'T12:00:00').getTime() : null, amt: Math.round(r.amount * 100) / 100, place: r.desc })),
        incomeIds: newIncomeEntries.map((e) => e.id),
        billIds: newBills.map((b) => b.id),
        subIds: newSubs.map((s) => s.id),
      },
    };
    stateUpdate.importHistory = [summary, ...(state.importHistory || [])].slice(0, 50);

    updateState(stateUpdate);

    const parts = [];
    if (selectedIncome.length)
      parts.push(`${selectedIncome.length} income entry${selectedIncome.length !== 1 ? 's' : ''}`);
    if (selectedRows.length)
      parts.push(`${selectedRows.length} transaction${selectedRows.length !== 1 ? 's' : ''}`);
    if (newBills.length) parts.push(`${newBills.length} bill${newBills.length !== 1 ? 's' : ''}`);
    if (newSubs.length)
      parts.push(`${newSubs.length} subscription${newSubs.length !== 1 ? 's' : ''}`);
    showToast(`✅ Imported ${parts.join(', ')}!`);
    onComplete?.(summary);
    onClose?.();
  };

  /* ── derived ───────────────────────────────────────────────────────────── */
  const allChecked = rows.length > 0 && rows.every((r) => r.checked);
  const checkedCount = rows.filter((r) => r.checked).length;
  const checkedTotal = rows.filter((r) => r.checked).reduce((s, r) => s + r.amount, 0);
  const allIncomeChecked = incomeRows.length > 0 && incomeRows.every((r) => r.checked);
  const checkedIncomeTotal = incomeRows.filter((r) => r.checked).reduce((s, r) => s + r.amount, 0);
  const acceptedSugs = sugs.filter((s) => sugOn[s.id]);
  const clarifyDone = ambig.every((_, i) => clarify[i] !== undefined);

  /* ════════════════════════════════════════════════════════════════════════ */
  const inner = (
    <div
      style={inline ? {} : {
        background: 'var(--surf)',
        border: '1px solid var(--bdr)',
        borderRadius: 16,
        width: '100%',
        maxWidth: 620,
        boxShadow: '0 8px 40px rgba(0,0,0,.5)',
        padding: 24,
        position: 'relative',
      }}
    >
        {/* Not-configured banner — shown when ANTHROPIC_API_KEY isn't set on the server */}
        {importAvailable === false && (
          <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.35)', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--red)', marginBottom: 4 }}>
              ⚠️ Import is not available
            </div>
            <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.6 }}>
              The AI import feature isn't configured on this server. An admin needs to set the <code style={{ background: 'var(--surf2)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>ANTHROPIC_API_KEY</code> environment variable and restart the server.
            </div>
          </div>
        )}

        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>📥 Import Transactions</div>
            <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>
              {step === S_INPUT &&
                'Paste bank text or upload a screenshot — AI will parse and classify everything'}
              {step === S_LOADING && 'Analyzing your statement…'}
              {step === S_CLARIFY &&
                `${ambig.length} item${ambig.length !== 1 ? 's' : ''} need your input before we continue`}
              {step === S_REVIEW && 'Review what will be imported, then confirm'}
            </div>
          </div>
          {!inline && (
            <button
              onClick={onClose}
              style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--mut)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Step indicator */}
        {step !== S_LOADING && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 18, alignItems: 'center' }}>
            {[
              ['1', 'Input', S_INPUT],
              ['2', 'Clarify', S_CLARIFY],
              ['3', 'Review', S_REVIEW],
            ].map(([_num, lbl, s], i) => {
              const steps = [S_INPUT, S_CLARIFY, S_REVIEW];
              const cur = steps.indexOf(step);
              const this_ = steps.indexOf(s);
              const active = step === s;
              const done = cur > this_;
              // Skip clarify indicator if no ambiguous items
              if (s === S_CLARIFY && ambig.length === 0 && step !== S_CLARIFY) return null;
              return (
                <React.Fragment key={s}>
                  {i > 0 && (
                    <div
                      style={{ flex: 1, height: 1, background: done ? 'var(--ylw)' : 'var(--bdr)' }}
                    />
                  )}
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 99,
                      background: active
                        ? 'var(--ylw)'
                        : done
                          ? 'rgba(255,217,61,.2)'
                          : 'var(--surf2)',
                      color: active ? '#000' : done ? 'var(--ylw)' : 'var(--mut)',
                      border: `1px solid ${active || done ? 'var(--ylw)' : 'var(--bdr)'}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {done ? '✓ ' : ''}
                    {lbl}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── STEP: INPUT ─────────────────────────────────────────────────── */}
        {step === S_INPUT && (
          <>
            {/* Prominent error banner — shown when a parse/API error comes back */}
            {error && (
              <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(255,107,107,.1)', border: '1.5px solid rgba(255,107,107,.35)', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--red)', marginBottom: 4 }}>
                      ⚠️ Something went wrong
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.6 }}>{error}</div>
                  </div>
                  <button
                    onClick={() => setError('')}
                    style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 16, flexShrink: 0, padding: 0 }}
                  >✕</button>
                </div>
                {/* Retry button — only show for API errors (longer messages) */}
                {error.length > 40 && (
                  <button
                    onClick={() => setError('')}
                    style={{ marginTop: 10, padding: '7px 14px', borderRadius: 8, background: 'rgba(255,107,107,.15)', border: '1px solid rgba(255,107,107,.4)', color: 'var(--red)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  >
                    ↩ Try Again
                  </button>
                )}
              </div>
            )}

            {/* ── Mode tabs ─────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--bdr)' }}>
              {[
                ['upload', '📁 Upload File'],
                ['paste',  '📋 Paste Text'],
              ].map(([id, lbl]) => (
                <button key={id} onClick={() => { setInputMode(id); setError(''); setSelectedFileName(''); }}
                  style={{ flex: 1, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: inputMode === id ? 'var(--ylw)' : 'var(--surf2)', color: inputMode === id ? '#000' : 'var(--mut)' }}>
                  {lbl}
                </button>
              ))}
            </div>

            {/* ── Upload tab ────────────────────────────────────────────── */}
            {inputMode === 'upload' && (
              <>
                {/* Format chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {[
                    ['PDF',          '📄', '#ff6b6b'],
                    ['CSV',          '📊', '#4d96ff'],
                    ['OFX / QFX',    '🏦', '#ffd93d'],
                    ['Screenshot',   '📸', '#ff6bdf'],
                    ['TXT / TSV',    '📝', '#7C6FF7'],
                  ].map(([label, em, col]) => (
                    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99, background: 'var(--surf2)', border: `1px solid ${col}33`, fontSize: 11, fontWeight: 600, color: 'var(--txt)' }}>
                      <span>{em}</span>{label}
                    </span>
                  ))}
                </div>

                {/* Drop zone */}
                <div
                  onClick={() => anyFileRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  style={{ border: `2px dashed ${dragOver ? 'var(--ylw)' : 'var(--bdr)'}`, borderRadius: 14, padding: '32px 20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'rgba(255,217,61,.06)' : 'var(--bg)', transition: 'all .15s' }}
                >
                  {selectedFileName ? (
                    <>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ylw)', marginBottom: 4 }}>{selectedFileName}</div>
                      <div style={{ fontSize: 11, color: 'var(--mut)' }}>Analyzing… or click to choose a different file</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Drop any bank file here</div>
                      <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.6 }}>
                        PDF · CSV · Excel (.xlsx) · OFX · QFX · Screenshot<br/>
                        or click to browse your files
                      </div>
                    </>
                  )}
                  <input
                    ref={anyFileRef}
                    type="file"
                    accept=".pdf,.csv,.xlsx,.xls,.ofx,.qfx,.tsv,.txt,.xml,.png,.jpg,.jpeg,.webp,.gif,.heic,image/*,application/pdf,text/csv,text/plain"
                    style={{ display: 'none' }}
                    onChange={(e) => handleAnyFile(e.target.files?.[0])}
                  />
                </div>

                {/* Tips row */}
                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--surf2)', fontSize: 11, color: 'var(--mut)', lineHeight: 1.7 }}>
                  💡 <strong style={{ color: 'var(--txt)' }}>Most accurate:</strong> CSV or OFX export from your bank's website.<br/>
                  Most banks: <em>Accounts → Transactions → Download / Export</em>
                </div>
              </>
            )}

            {/* ── Paste tab ─────────────────────────────────────────────── */}
            {inputMode === 'paste' && (
              <>
                <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 6 }}>
                  Copy your transaction list from your bank's website and paste it here. Any format works — dates, amounts, descriptions.
                </div>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={'01/15  Amazon.com              -$42.99\n01/16  Chipotle                 -$13.50\n01/17  Chevron Gas               -$55.00\n01/18  Netflix                   -$15.99\n...'}
                  style={{ width: '100%', minHeight: 170, padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--bdr)', background: 'var(--bg)', color: 'var(--txt)', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }}
                />
                <button
                  onClick={handlePaste}
                  disabled={!pasteText.trim()}
                  style={{ marginTop: 12, width: '100%', padding: '11px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: pasteText.trim() ? 'pointer' : 'not-allowed', border: 'none', background: pasteText.trim() ? 'var(--ylw)' : 'var(--surf2)', color: pasteText.trim() ? '#000' : 'var(--mut)' }}
                >
                  Analyze Transactions →
                </button>
              </>
            )}
          </>
        )}

        {/* ── STEP: LOADING ───────────────────────────────────────────────── */}
        {step === S_LOADING && (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div
              style={{
                fontSize: 40,
                marginBottom: 16,
                display: 'inline-block',
                animation: 'spin 1.2s linear infinite',
              }}
            >
              ⚙️
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              Analyzing your statement…
            </div>
            <div style={{ fontSize: 12, color: 'var(--mut)' }}>
              Claude is classifying transactions and looking for savings opportunities.
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* ── STEP: CLARIFY ───────────────────────────────────────────────── */}
        {step === S_CLARIFY && (
          <>
            <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 14 }}>
              These charges weren't clearly one-time expenses.{' '}
              <strong style={{ color: 'var(--txt)' }}>All items must be classified</strong> before
              you can continue.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {ambig.map((item, i) => {
                const dec = clarify[i];
                const opts = [
                  ['variable', '📊', 'Variable spend', 'One-time purchase'],
                  ['bill', '📋', 'Fixed bill', 'Recurring monthly charge'],
                  ['sub', '📱', 'Subscription', 'Digital/streaming service'],
                  ['skip', '⏭️', 'Skip', "Don't import this"],
                ];
                return (
                  <div
                    key={i}
                    style={{
                      border: `1.5px solid ${dec ? 'var(--ylw)' : 'var(--bdr)'}`,
                      borderRadius: 12,
                      padding: '12px 14px',
                      background: dec ? 'rgba(255,217,61,.04)' : 'var(--bg)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{item.description}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--red)' }}>
                        {fc(item.amount)}
                      </div>
                    </div>
                    {item.hint && (
                      <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 8 }}>
                        💬 {item.hint}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {opts.map(([val, em, lbl]) => (
                        <button
                          key={val}
                          onClick={() => setClarify((c) => ({ ...c, [i]: val }))}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            border: `1.5px solid ${dec === val ? 'var(--ylw)' : 'var(--bdr)'}`,
                            background: dec === val ? 'var(--ylw)' : 'var(--surf2)',
                            color: dec === val ? '#000' : 'var(--txt)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <span>{em}</span>
                          <span>{lbl}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={finishClarify}
              disabled={!clarifyDone}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                cursor: clarifyDone ? 'pointer' : 'not-allowed',
                border: 'none',
                background: clarifyDone ? 'var(--ylw)' : 'var(--surf2)',
                color: clarifyDone ? '#000' : 'var(--mut)',
              }}
            >
              {clarifyDone
                ? 'Continue to Review →'
                : `Answer all ${ambig.filter((_, i) => !clarify[i]).length} remaining items to continue`}
            </button>
          </>
        )}

        {/* ── STEP: REVIEW ────────────────────────────────────────────────── */}
        {step === S_REVIEW && (
          <>
            {/* Duplicate warning banner */}
            {(() => {
              const dupeCount = rows.filter((r) => r.isDuplicate).length;
              if (!dupeCount) return null;
              return (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: 'rgba(255,217,61,.08)',
                  border: '1.5px solid rgba(255,217,61,.35)',
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ylw)', marginBottom: 3 }}>
                      {dupeCount} possible duplicate{dupeCount !== 1 ? 's' : ''} detected
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.5 }}>
                      These transactions match something already in your spending log — they've been unchecked.
                      Review the <span style={{ color: 'var(--ylw)', fontWeight: 600 }}>⟳</span> rows below and check any that are actually new.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Income section */}
            {incomeRows.length > 0 && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700 }}>
                    💰 Income Detected —{' '}
                    <span style={{ color: 'var(--grn)' }}>
                      {incomeRows.length} deposit{incomeRows.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      setIncomeRows(incomeRows.map((r) => ({ ...r, checked: !allIncomeChecked })))
                    }
                    style={{
                      fontSize: 11,
                      padding: '3px 9px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      border: '1px solid var(--bdr)',
                      background: 'none',
                      color: 'var(--mut)',
                      fontWeight: 600,
                    }}
                  >
                    {allIncomeChecked ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div
                  style={{
                    border: '1px solid rgba(107,203,119,.35)',
                    borderRadius: 10,
                    marginBottom: 14,
                    overflow: 'hidden',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'rgba(107,203,119,.08)' }}>
                        <th style={{ padding: '7px 8px', width: 28 }}></th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'left',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'left',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Description
                        </th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'right',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeRows.map((row, i) => (
                        <tr
                          key={row.id}
                          style={{
                            borderTop: '1px solid rgba(107,203,119,.2)',
                            opacity: row.checked ? 1 : 0.4,
                          }}
                        >
                          <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                            <input
                              type="checkbox"
                              checked={row.checked}
                              onChange={(e) =>
                                setIncomeRows(
                                  incomeRows.map((r, j) =>
                                    j === i ? { ...r, checked: e.target.checked } : r
                                  )
                                )
                              }
                              style={{
                                width: 14,
                                height: 14,
                                cursor: 'pointer',
                                accentColor: 'var(--grn)',
                              }}
                            />
                          </td>
                          <td
                            style={{
                              padding: '6px 8px',
                              color: 'var(--mut)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.date
                              ? (() => {
                                  const d = new Date(
                                    row.date.includes('T') ? row.date : row.date + 'T12:00:00'
                                  );
                                  return isNaN(d)
                                    ? row.date
                                    : d.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                      });
                                })()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', color: 'var(--txt)' }}>{row.desc}</td>
                          <td
                            style={{
                              padding: '6px 8px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: 'var(--grn)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            +{fc(row.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Variable spend table */}
            {rows.length > 0 && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700 }}>
                    📊 Variable Spending —{' '}
                    <span style={{ color: 'var(--grn)' }}>{rows.length} transactions</span>
                  </div>
                  <button
                    onClick={() => setRows(rows.map((r) => ({ ...r, checked: !allChecked })))}
                    style={{
                      fontSize: 11,
                      padding: '3px 9px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      border: '1px solid var(--bdr)',
                      background: 'none',
                      color: 'var(--mut)',
                      fontWeight: 600,
                    }}
                  >
                    {allChecked ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div
                  style={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    border: '1px solid var(--bdr)',
                    borderRadius: 10,
                    marginBottom: 14,
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'var(--surf2)', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '7px 8px', width: 28 }}></th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'left',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'left',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Description
                        </th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'right',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Amount
                        </th>
                        <th
                          style={{
                            padding: '7px 8px',
                            textAlign: 'left',
                            color: 'var(--mut)',
                            fontWeight: 700,
                          }}
                        >
                          Category
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr
                          key={row.id}
                          style={{
                            borderTop: '1px solid var(--bdr)',
                            opacity: row.checked ? 1 : row.isDuplicate ? 0.55 : 0.4,
                            background: row.isDuplicate
                              ? 'rgba(255,217,61,.04)'
                              : row.checked ? 'transparent' : 'rgba(122,122,154,.04)',
                          }}
                        >
                          <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                            <input
                              type="checkbox"
                              checked={row.checked}
                              onChange={(e) =>
                                setRows(
                                  rows.map((r, j) =>
                                    j === i ? { ...r, checked: e.target.checked } : r
                                  )
                                )
                              }
                              style={{
                                width: 14,
                                height: 14,
                                cursor: 'pointer',
                                accentColor: 'var(--ylw)',
                              }}
                            />
                          </td>
                          <td
                            style={{
                              padding: '6px 8px',
                              color: 'var(--mut)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.date
                              ? (() => {
                                  const d = new Date(
                                    row.date.includes('T') ? row.date : row.date + 'T12:00:00'
                                  );
                                  return isNaN(d)
                                    ? row.date
                                    : d.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                      });
                                })()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', maxWidth: 150 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {row.isDuplicate && (
                                <span
                                  title="Possible duplicate — already in your spending log"
                                  style={{ fontSize: 10, color: 'var(--ylw)', fontWeight: 700, flexShrink: 0 }}
                                >
                                  ⟳
                                </span>
                              )}
                              <input
                                value={row.desc}
                                onChange={(e) =>
                                  setRows(
                                    rows.map((r, j) => (j === i ? { ...r, desc: e.target.value } : r))
                                  )
                                }
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  borderBottom: '1px dashed var(--bdr)',
                                  color: 'var(--txt)',
                                  fontSize: 11,
                                  fontFamily: 'inherit',
                                  width: '100%',
                                  padding: '1px 0',
                                }}
                              />
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '6px 8px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: 'var(--red)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {fc(row.amount)}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <select
                                value={row.catId}
                                onChange={(e) =>
                                  setRows(rows.map((r, j) => j === i ? { ...r, catId: e.target.value } : r))
                                }
                                style={{ background: 'var(--bg)', border: '1px solid var(--bdr)', borderRadius: 5, color: 'var(--txt)', fontSize: 10, padding: '3px 5px', cursor: 'pointer', fontFamily: 'inherit' }}
                              >
                                {cats.map((c) => (
                                  <option key={c.id} value={c.id}>{c.em} {c.name}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => {
                                  const keyword = normDesc(row.desc).split(' ').filter(w => w.length > 2)[0];
                                  if (!keyword) return;
                                  const exists = importRules.some(r => r.match === keyword && r.catId === row.catId);
                                  if (exists) return;
                                  updateState({ importRules: [...importRules, { id: 'rule_' + Date.now(), match: keyword, catId: row.catId }] });
                                  setRows(rows.map((r, j) => j === i ? { ...r, _ruleSaved: true } : r));
                                }}
                                title={`Always categorize "${normDesc(row.desc).split(' ').filter(w=>w.length>2)[0]}" as this category`}
                                style={{ fontSize: 10, padding: '2px 5px', borderRadius: 4, border: '1px solid var(--bdr)', background: row._ruleSaved ? 'rgba(107,203,119,.15)' : 'var(--surf2)', color: row._ruleSaved ? 'var(--grn)' : 'var(--mut)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                              >
                                {row._ruleSaved ? '✓' : '📌'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals bar */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'var(--bg)',
                    borderRadius: 8,
                    border: '1px solid var(--bdr)',
                    marginBottom: incomeRows.length > 0 ? 8 : 16,
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>
                    <strong style={{ color: 'var(--txt)' }}>{checkedCount}</strong> of {rows.length}{' '}
                    selected
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>
                    −{fc(checkedTotal)}
                  </div>
                </div>
              </>
            )}

            {/* Net cash flow summary — only when both income and spending exist */}
            {incomeRows.length > 0 &&
              rows.length > 0 &&
              (() => {
                const net = checkedIncomeTotal - checkedTotal;
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderRadius: 10,
                      marginBottom: 16,
                      background: net >= 0 ? 'rgba(107,203,119,.08)' : 'rgba(255,107,107,.08)',
                      border: `1px solid ${net >= 0 ? 'rgba(107,203,119,.3)' : 'rgba(255,107,107,.3)'}`,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      📊 Net this statement
                      <div
                        style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 400, marginTop: 2 }}
                      >
                        {fc(checkedIncomeTotal)} income − {fc(checkedTotal)} spending
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: net >= 0 ? 'var(--grn)' : 'var(--red)',
                      }}
                    >
                      {net >= 0 ? '+' : '−'}
                      {fc(Math.abs(net))}
                    </div>
                  </div>
                );
              })()}

            {/* Suggestions */}
            {sugs.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 8,
                    paddingTop: rows.length ? 0 : 0,
                  }}
                >
                  💡 Suggestions from your statement
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {sugs.map((s) => {
                    const cfg = SUG_CFG[s.type] || SUG_CFG.add_bill;
                    const on = !!sugOn[s.id];
                    return (
                      <div
                        key={s.id}
                        onClick={() => setSugOn((o) => ({ ...o, [s.id]: !o[s.id] }))}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                          padding: '10px 14px',
                          borderRadius: 10,
                          cursor: 'pointer',
                          border: `1.5px solid ${on ? cfg.color : 'var(--bdr)'}`,
                          background: on ? `rgba(${hexToRgb(cfg.color)}, 0.07)` : 'var(--bg)',
                          transition: 'all .12s',
                        }}
                      >
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 5,
                            border: `2px solid ${on ? cfg.color : 'var(--bdr)'}`,
                            background: on ? cfg.color : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            color: on ? '#000' : 'transparent',
                            flexShrink: 0,
                            marginTop: 1,
                          }}
                        >
                          ✓
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginBottom: 2,
                            }}
                          >
                            <span style={{ fontSize: 14 }}>{cfg.em}</span>
                            <span style={{ fontSize: 12, fontWeight: 700 }}>{s.name}</span>
                            {s.type !== 'budget_insight' && s.amount > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--mut)' }}>
                                · {fc(s.amount)}/mo
                              </span>
                            )}
                            <span
                              style={{
                                marginLeft: 'auto',
                                fontSize: 10,
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: 99,
                                background: on ? cfg.color : 'var(--surf2)',
                                color: on ? '#000' : 'var(--mut)',
                              }}
                            >
                              {cfg.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--mut)' }}>{s.reason}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Empty state */}
            {rows.length === 0 && incomeRows.length === 0 && sugs.length === 0 && (
              <div
                style={{ textAlign: 'center', padding: '32px', color: 'var(--mut)', fontSize: 13 }}
              >
                Nothing to import — all items were skipped.
              </div>
            )}

            {/* Action buttons */}
            {(() => {
              const hasAnything =
                checkedCount > 0 || incomeRows.some((r) => r.checked) || acceptedSugs.length > 0;
              return (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleConfirm}
                      disabled={!hasAnything}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 700,
                        border: 'none',
                        cursor: hasAnything ? 'pointer' : 'not-allowed',
                        background: hasAnything ? 'var(--ylw)' : 'var(--surf2)',
                        color: hasAnything ? '#000' : 'var(--mut)',
                      }}
                    >
                      ✅ Confirm Import
                      {hasAnything && (
                        <span
                          style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, opacity: 0.8 }}
                        >
                          (
                          {[
                            incomeRows.filter((r) => r.checked).length > 0 &&
                              `${incomeRows.filter((r) => r.checked).length} income`,
                            checkedCount > 0 && `${checkedCount} tx`,
                            acceptedSugs.filter((s) => s.type === 'add_bill').length > 0 &&
                              `${acceptedSugs.filter((s) => s.type === 'add_bill').length} bills`,
                            acceptedSugs.filter((s) => s.type === 'add_subscription').length > 0 &&
                              `${acceptedSugs.filter((s) => s.type === 'add_subscription').length} subs`,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                          )
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setStep(ambig.length > 0 ? S_CLARIFY : S_INPUT);
                      }}
                      style={{
                        padding: '12px 16px',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        border: '1px solid var(--bdr)',
                        background: 'none',
                        color: 'var(--mut)',
                        cursor: 'pointer',
                      }}
                    >
                      ← Back
                    </button>
                  </div>
                  <div
                    style={{ marginTop: 8, fontSize: 10, color: 'var(--mut)', textAlign: 'center' }}
                  >
                    Transactions import to the month matching their date. Edit descriptions above
                    before confirming.
                  </div>
                </>
              );
            })()}
          </>
        )}
    </div>
  );

  if (inline) return inner;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 12px' }}>
      {inner}
    </div>
  );
}

/* ── hex color → "r, g, b" for rgba() ────────────────────────────────────── */
function hexToRgb(hex) {
  if (!hex || hex.startsWith('var(')) return '100,100,100';
  const h = hex.replace('#', '');
  if (h.length === 3) {
    const [r, g, b] = h.split('').map((c) => parseInt(c + c, 16));
    return `${r},${g},${b}`;
  }
  const r = parseInt(h.slice(0, 2), 16),
    g = parseInt(h.slice(2, 4), 16),
    b = parseInt(h.slice(4, 6), 16);
  return `${r},${g},${b}`;
}
